import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  List,
  ListItem,
  Snackbar,
  SvgIcon,
} from '@mui/material';
import { getUserType } from 'entities/user';
import { useUser } from 'shared/api/tanstack';
import { DashboardHeaderBackLink } from 'widgets/dashboard-header-back-link';
import styles from './user-profile.module.scss';

const userTypeMap = (userType?: string) => {
  switch (userType) {
    case 'agent':
      return 'Агент';
    case 'supplier':
      return 'Поставщик';
    case 'leasing':
      return 'Лизинговая компания';
    case 'client':
      return 'Клиент';
    default:
      return '-';
  }
};

const userSubtypeMap = (userSubtype?: string) => {
  switch (userSubtype) {
    case 'legal':
      return 'Юридическое лицо';
    case 'entrepreneur':
      return 'Индивидуальный предприниматель';
    case 'individual':
      return 'Физическое лицо';
    case 'self-employed':
      return 'Самозанятый';
    default:
      return '-';
  }
};

export const UserProfile = () => {
  const { data: user } = useUser();
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const referralLink = user?.referal ? `${window.location.origin}/referal?id=${user.referal}` : '';

  const handleCopy = async () => {
    if (referralLink) {
      try {
        await navigator.clipboard.writeText(referralLink);
        setSnackbarOpen(true);
      } catch (err) {
        console.error('Ошибка при копировании:', err);
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  if (!user) {
    return (
      <Box className={styles.userProfilePage}>
        <Box className={styles.card}>
          <CardHeader title='Загрузка...' />
        </Box>
      </Box>
    );
  }

  return (
    <Box className={styles.userProfilePage}>
      <DashboardHeaderBackLink link={-1} title='Профиль пользователя' />
      <Box>
        <Card className={styles.card}>
          <CardHeader title={user.bio || 'Профиль пользователя'} />
          <CardContent className={styles.cardContent}>
            <List>
              <ListItem className={styles.listItem}>
                <b>Название/ФИО:</b>
                <span>{user.bio || '-'}</span>
              </ListItem>
              {user.email && (
                <ListItem className={styles.listItem}>
                  <b>Почта:</b>
                  <span>{user.email}</span>
                </ListItem>
              )}
              {user.phone && (
                <ListItem className={styles.listItem}>
                  <b>Телефон:</b>
                  <span>{user.phone}</span>
                </ListItem>
              )}
              {user.inn && (
                <ListItem className={styles.listItem}>
                  <b>ИНН:</b>
                  <span>{user.inn}</span>
                </ListItem>
              )}
              <ListItem className={styles.listItem}>
                <b>Тип пользователя:</b>
                <span>{getUserType(user)}</span>
              </ListItem>
              {((user as any).user_subtype || user.user_sub_type) && (
                <ListItem className={styles.listItem}>
                  <b>Тип клиента:</b>
                  <span>{userSubtypeMap((user as any).user_subtype || user.user_sub_type)}</span>
                </ListItem>
              )}
              {user.user_type === 'agent' && user.share && (
                <ListItem className={styles.listItem}>
                  <b>Доля:</b>
                  <span>{user.share}</span>
                </ListItem>
              )}
              <hr style={{ margin: '10px 0', border: '1px solid #E0E0E0' }} />
              {user.inn && (
                <ListItem className={styles.listItem}>
                  <b>Реферальная ссылка:</b>
                  <Box className={styles.referralLinkWrapper}>
                    <span className={styles.referralLinkText}>{referralLink}</span>
                    <IconButton
                      onClick={handleCopy}
                      className={styles.copyButton}
                      size='small'
                      disableRipple
                    >
                      <SvgIcon sx={{ fontSize: '18px', color: '#4794EF' }} viewBox='0 0 24 24'>
                        <path d='M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z' />
                      </SvgIcon>
                    </IconButton>
                  </Box>
                </ListItem>
              )}
              {user.inviter_bio && (
                <>
                  <hr style={{ margin: '10px 0', border: '1px solid #E0E0E0' }} />
                  <ListItem className={styles.listItem}>
                    <b>Пригласивший(наименование):</b>
                    <span>{user.inviter_bio}</span>
                  </ListItem>
                  <ListItem className={styles.listItem}>
                    <b>Пригласивший(ИНН):</b>
                    <span>{user.inviter}</span>
                  </ListItem>
                </>
              )}

              {user.invitees_count > 0 && (
                <>
                  <hr style={{ margin: '10px 0', border: '1px solid #E0E0E0' }} />
                  <ListItem className={styles.listItem}>
                    <b>Приглашенно по ссылке:</b>
                    <span>{user.invitees_count}</span>
                  </ListItem>
                  <List className={styles.inviteeItemWrapper}>
                    {user.invitees.map((invitee: any, index: number) => (
                      <ListItem className={styles.inviteeItem} key={index}>
                        <span>{index + 1}.</span>
                        <div className={styles.inviteeItemData}>
                          <span>
                            <b>Наименование:</b> {invitee.bio}
                          </span>
                          <span>
                            <b>ИНН:</b> {invitee.inn}
                          </span>
                        </div>
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            </List>
          </CardContent>
        </Card>
      </Box>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message='Ссылка скопирована в буфер обмена'
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};
